import React from 'react';
import { Table } from '@rd-web-markets/shared/dist/util';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import SendInvitationEmail from './SendConfirmationInstructions';

const UserList = ({ users, usersType, handleDelete, onUsersChange, params, setParams, handleActivate }) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const history = useHistory();
  const isConsultantOrAdminOrManager = ['consultant', 'admin', 'manager'].includes(loggedInUser.account_type);
  const { t } = useTranslation();
  if (!users) return <Table></Table>;

  const renderUserContacts = user =>
    <>
      <span><span className="material-icons">mail</span> <a href={'mailto:' + user.email}>{user.email}</a></span>
      <br />
      <span><span className="material-icons">call</span> {user.phone_number}</span>;
    </>;

  const customerDisplayFields = window.MARKET_CONSTANTS.USER_PAGE_CUSTOMER_DISPLAY_FIELDS[loggedInUser.account_type] || [];

  // Helper function to create sortable header with default values
  const createSortableHeader = (text, orderBy, sortable = true) => {
    const orderByValue = params.order_by?.split(' ')[0];
    const orderDirection = params.order_by?.split(' ')[1];
    const isActive = orderByValue === orderBy;
    
    return {
      text: t(text),
      sortable,
      orderBy,
      ...(isActive && { order: orderDirection })
    };
  };

  // Customer headers
  const customerHeaders = [
    customerDisplayFields.includes('name') ? createSortableHeader('name', 'name') : null,
    customerDisplayFields.includes('role') ? createSortableHeader('role', 'role') : null,
    customerDisplayFields.includes('contact') ? { text: t('contact') } : null,
    customerDisplayFields.includes('is_active') ? { text: t('active') } : null,
  ].filter(h => h !== null);

  // Other users headers
  const otherUsersHeaders = [
    createSortableHeader('name', 'name'),
    createSortableHeader('role', 'role'),
    { text: t('contact') },
    { text: t('active') },
  ];

  const headers = usersType === 'customer' ? customerHeaders : otherUsersHeaders;

  if (isConsultantOrAdminOrManager) {
    headers.push({ text: '' });
  }

  const userActiveText = user => {
    return user.is_active ? <p className="text-success">Active User</p> : <p className="text-danger">'Not an active user'</p>;
  }

  // Helper function to determine if manager can edit/delete a user
  const canManagerModifyUser = (user) => {
    if (loggedInUser.account_type !== 'manager') return true;
    // Manager can only modify consultant and customer users
    return ['consultant', 'customer'].includes(user.account_type);
  };

  // Helper function to create customer row columns
  const createCustomerRowColumns = (user) => {
    const activeCheckbox = (
      <Form.Check type='checkbox'>
        <Form.Check.Input
          type='checkbox'
          disabled={loggedInUser.account_type !== 'admin'}
          checked={user.is_active}
          value={user.is_active}
          name='is_active'
          onChange={(e) => handleActivate(e, user.id)}
        />
        <Form.Check.Label>{userActiveText(user)}</Form.Check.Label>
      </Form.Check>
    );

    return [
      customerDisplayFields.includes('name') ? user.name : null,
      customerDisplayFields.includes('role') ? user.role || '-' : null,
      customerDisplayFields.includes('contact') ? renderUserContacts(user) : null,
      customerDisplayFields.includes('is_active') ? activeCheckbox : null,
    ].filter(c => c !== null);
  };

  // Helper function to create other users row columns
  const createOtherUsersRowColumns = (user) => {
    return [
      user.name,
      user.role || '-',
      renderUserContacts(user),
      <Form.Check type='checkbox'>
        <Form.Check.Input
          type='checkbox'
          disabled={loggedInUser.account_type !== 'admin'}
          checked={user.is_active}
          value={user.is_active}
          name='is_active'
          onChange={(e) => handleActivate(e, user.id)}
        />
        <Form.Check.Label>{userActiveText(user)}</Form.Check.Label>
      </Form.Check>
    ];
  };

  const rows = users.map(u => {
    const canModify = canManagerModifyUser(u);
    
    const row = {
      key: u.id,
      columns: usersType === 'customer' 
        ? createCustomerRowColumns(u)
        : createOtherUsersRowColumns(u),
      // Use showEdit and showDelete to hide buttons (Table component checks these props)
      showEdit: canModify,
      showDelete: canModify
    };

    if (isConsultantOrAdminOrManager) {
      row.columns.push(<SendInvitationEmail user={u} onSend={onUsersChange} />);
    }

    return row;
  });

  const sortUsers = (eventKey, direction) => {
    setParams({...params, order_by: headers[eventKey].orderBy + ' ' + direction });
  };

  return (
    <>
      <Table
        headers={headers}
        rows={rows}
        onEdit={i => history.push(`/admin/users/${users[i].id}/edit`)}
        onSort={(i, d) => sortUsers(i, d)}
        onDelete={i => handleDelete(users[i].id)}
      />
    </>
  );
};

export default UserList
